import React, { useEffect, useRef } from 'react';
import {
  Paper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Fade,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

export default function MentionSuggestions({
  isVisible,
  participants,
  selectedIndex,
  onSelect,
  getDisplayName,
  compact = false,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!isVisible || !participants?.length) return null;

  return (
    <Fade in timeout={150}>
      <Paper
        ref={listRef}
        elevation={8}
        sx={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
          mb: 0.5,
          maxHeight: compact ? 160 : 220,
          overflowY: 'auto',
          zIndex: 1300,
          borderRadius: 1,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        }}
      >
        <List dense disablePadding>
          {participants.map((participant, index) => {
            const displayName = getDisplayName(participant);
            const identifier =
              participant.username || participant.creator_id || participant.id || '';
            const avatarUrl =
              participant.pfp ||
              participant.profile_photo ||
              participant.phphref ||
              participant.pfphref ||
              '';
            const isSelected = index === selectedIndex;

            return (
              <ListItemButton
                key={participant.global_user_id || participant.id || index}
                ref={isSelected ? selectedRef : null}
                selected={isSelected}
                onClick={() => onSelect(participant)}
                dense
                sx={{
                  py: compact ? 0.5 : 0.75,
                  px: compact ? 1 : 1.5,
                  '&.Mui-selected': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
                    },
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: compact ? 32 : 40 }}>
                  <Avatar
                    src={avatarUrl}
                    sx={{
                      width: compact ? 24 : 32,
                      height: compact ? 24 : 32,
                      fontSize: compact ? '0.7rem' : '0.85rem',
                    }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: compact ? '0.8rem' : '0.875rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {displayName}
                    </Typography>
                  }
                  secondary={
                    identifier && String(identifier) !== displayName ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: compact ? '0.65rem' : '0.75rem' }}
                      >
                        @{identifier}
                      </Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>
    </Fade>
  );
}
